import { useEffect, useRef } from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { ActivityIndicator } from 'react-native-paper';
import { useRouter } from 'expo-router';
import Toast from 'react-native-toast-message';
import * as Crypto from 'expo-crypto';
import SheetModal from '@/components/General/SheetModal';
import Callout from '@/components/General/Callout';
import Unicon from '@/components/General/Unicon';
import { Query } from '@/utils/react-query/Queries';
import { Mutation } from '@/utils/react-query/Mutations';

/**
 * StartPathSheet — pick an active structured Path template and start it for
 * a person via follow_up_start_path (spec §6.1/§12.4). Idempotent start: an
 * already-open enrollment returns alreadyOpen and we navigate straight to
 * the existing enrollment rather than showing an error (documented RPC
 * behavior — never present it as a failure).
 *
 * A fresh idempotencyKey is generated per sheet open so a retried submit is
 * safe while a brand-new intentional start is never deduplicated away.
 */
const StartPathSheet = ({ isOpen, onClose, organizationID, personID, personName }) => {
    const router = useRouter();
    const idempotencyKeyRef = useRef(null);

    const { data: paths = [], isLoading } = Query.followUp.paths({
        organizationID,
        options: { enabled: isOpen && !!organizationID },
    });

    // Active templates only — there is one flat Path model; Care starts
    // without a template via follow_up_start_care (§25.9).
    const startable = (paths || []).filter(
        (p) => p.isActive !== false && !p.isArchived
    );

    const startMutation = Mutation.followUp.startPath({
        onSuccess: (data) => {
            const enrollmentID = data?.enrollmentID;
            Toast.show({
                type: data?.alreadyOpen ? 'info' : 'success',
                text2: data?.alreadyOpen
                    ? 'This person is already on that path — opening their progress.'
                    : 'Path started',
            });
            onClose?.();
            if (enrollmentID) {
                router.push(`/follow-up/enrollments/${enrollmentID}`);
            }
        },
        onError: (error) => {
            if (!error?.expected) {
                Toast.show({ type: 'error', text2: 'Failed to start the path. Please try again.' });
            }
        },
    });

    // Fresh idempotency key per open session — a retried submit replays
    // server-side instead of duplicating the enrollment.
    useEffect(() => {
        if (isOpen) {
            idempotencyKeyRef.current = Crypto.randomUUID();
        } else {
            idempotencyKeyRef.current = null;
        }
    }, [isOpen]);

    const handlePick = (path) => {
        if (startMutation.isPending || !personID || !path?.pathID) return;
        startMutation.mutate({
            organizationID,
            personID,
            pathID: path.pathID,
            idempotencyKey: idempotencyKeyRef.current,
        });
    };

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={onClose}
            title="Start a Path"
            subtitle={personName ? `for ${personName}` : undefined}
            stayOpenOnBlur
            snapPoints={[420, 620]}
            cancelText="Cancel"
        >
            {isLoading ? (
                <View style={styles.loadingWrap}><ActivityIndicator /></View>
            ) : startable.length === 0 ? (
                <View style={styles.emptyWrap} testID="start-path-empty">
                    <Callout
                        type="info"
                        message="No active structured paths are available. A Follow Up manager can create one under Follow Up → Paths."
                    />
                </View>
            ) : (
                <ScrollView style={styles.list} keyboardShouldPersistTaps="handled">
                    {startable.map((path) => (
                        <Pressable
                            key={path.pathID}
                            testID={`start-path-${path.pathID}`}
                            accessibilityRole="button"
                            disabled={startMutation.isPending}
                            onPress={() => handlePick(path)}
                            style={styles.pathRow}
                        >
                            <View style={styles.pathIcon}>
                                <Unicon name="git-branch-outline" size={16} colorVariant="paths" />
                            </View>
                            <View style={styles.pathBody}>
                                <Text style={styles.pathName} numberOfLines={1}>{path.name}</Text>
                                <Text style={styles.pathMeta} numberOfLines={1}>
                                    {`${path.stepCount ?? 0} step${path.stepCount === 1 ? '' : 's'}`}
                                    {path.activeEnrollmentCount > 0
                                        ? ` · ${path.activeEnrollmentCount} enrolled`
                                        : ''}
                                </Text>
                            </View>
                            {startMutation.isPending && startMutation.variables?.pathID === path.pathID ? (
                                <ActivityIndicator size="small" />
                            ) : (
                                <Unicon name="chevron-forward-outline" size={16} colorVariant="textTertiary" />
                            )}
                        </Pressable>
                    ))}
                </ScrollView>
            )}
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    loadingWrap: { padding: 32, alignItems: 'center' },
    emptyWrap: { paddingBottom: 16 },
    list: { flexGrow: 0, paddingBottom: 8 },
    pathRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 12,
        marginBottom: 8,
        boxShadow: shadows.card,
        _web: {
            cursor: 'pointer',
            transition: 'all 0.15s ease-in-out',
            _hover: { backgroundColor: colors.hover },
        },
    },
    pathIcon: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: colors.pathsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    pathBody: { flex: 1, minWidth: 0 },
    pathName: { fontSize: 14, fontFamily: fonts.bold, color: colors.text },
    pathMeta: { fontSize: 12, color: colors.textSecondary, marginTop: 1 },
}));

export default StartPathSheet;
